import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Container from 'components/widgets/Container';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useLocation } from 'react-router-dom';

const MESSAGE_TYPE_OPEN_COLLECTION_ACCOUNT = 'OPEN_COLLECTION_ACCOUNT';

const useStyles = makeStyles(() => ({
    boxContainer: {
        height: '100%',
        width: '100%',
        padding: 0,
        margin: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    panelContainer: {
        position: 'relative',
        flex: 1,
        minHeight: 0,
        width: '100%',
        overflow: 'hidden',
    },
    iframe: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        border: 0,
        display: 'block',
    },
}));

/**
 * URL style:
 * - 'hash': baseURL/#submodule?key=token (e.g. HR)
 * - 'path': baseURL/submodule?key=token
 * - 'login-hash': baseURL/login-hash?token=token#submodule (e.g. Collections - hash indicates redirect route)
 */
const ExternalModuleIframe = ({
    moduleURL,
    getToken,
    moduleTitle = 'External Module',
    urlStyle = 'hash',
    openAccountPath = null,
}) => {
    const classes = useStyles();
    const { submodule } = useParams();
    const { search } = useLocation();
    const token = getToken ? getToken() : null;
    const parentSearchParams = new URLSearchParams(search);
    const accountNumberFromUrl = parentSearchParams.get('accountNumber');

    useEffect(() => {
        const shouldSetup = openAccountPath && moduleURL;
        if (!shouldSetup) return () => {};

        let iframeOrigin;
        try {
            iframeOrigin = new URL(moduleURL).origin;
        } catch {
            return () => {};
        }

        const handleMessage = (event) => {
            if (event.origin !== iframeOrigin) return;
            const { type, accountNumber } = event.data || {};
            if (type !== MESSAGE_TYPE_OPEN_COLLECTION_ACCOUNT || accountNumber == null) return;

            const id = parseInt(accountNumber, 10);
            if (Number.isNaN(id)) return;

            const basePath = openAccountPath.replace(/\/$/, '');
            const url = `${window.location.origin}${basePath}?accountNumber=${id}`;
            window.open(url, '_blank');
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [moduleURL, openAccountPath]);

    if (!moduleURL) {
        return null;
    }

    const base = moduleURL.replace(/\/$/, '');
    let iFrameSrc;

    if (urlStyle === 'login-hash') {
        const params = new URLSearchParams();
        if (token) params.set('token', token);
        if (openAccountPath) params.set('parentOrigin', window.location.origin);
        if (accountNumberFromUrl) params.set('accountNumber', accountNumberFromUrl);
        const queryString = params.toString();
        const tokenParam = queryString ? `?${queryString}` : '';
        const hash = submodule ? `#${submodule}` : '';
        iFrameSrc = `${base}/login-hash${tokenParam}${hash}`;
    } else {
        const query = token ? `?key=${token}` : '';
        if (submodule) {
            if (urlStyle === 'hash') {
                iFrameSrc = `${base}/#${submodule}${query}`;
            } else {
                iFrameSrc = `${base}/${submodule}${query}`;
            }
        } else {
            iFrameSrc = `${base}${query}`;
        }
    }

    return (
        <Container className={classes.boxContainer} disableGutters maxWidth={false}>
            <div className={classes.panelContainer}>
                <iframe
                    className={classes.iframe}
                    key={iFrameSrc}
                    src={iFrameSrc}
                    title={moduleTitle}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    allow="clipboard-read; clipboard-write"
                />
            </div>
        </Container>
    );
};

ExternalModuleIframe.propTypes = {
    moduleURL: PropTypes.string,
    getToken: PropTypes.func,
    moduleTitle: PropTypes.string,
    urlStyle: PropTypes.oneOf(['hash', 'path', 'login-hash']),
    openAccountPath: PropTypes.string,
};

ExternalModuleIframe.defaultProps = {
    moduleURL: null,
    getToken: null,
    moduleTitle: 'External Module',
    urlStyle: 'hash',
    openAccountPath: null,
};

export { MESSAGE_TYPE_OPEN_COLLECTION_ACCOUNT };

export default ExternalModuleIframe;
